// Download file from url
export async function download(url: string, filename?: string) {
	const response = await fetch(url); // CORS is now handled by S3!
	const blob = await response.blob();
	const blobUrl = window.URL.createObjectURL(blob);

	const a = document.createElement('a');
	a.href = blobUrl;

	// 1. Use the provided filename, OR
	// 2. Parse the URL, grab the path (ignoring query params), and get the last piece
	const derivedFilename = filename || new URL(url).pathname.split('/').pop();

	// Only set the download attribute if we successfully resolved a name
	if (derivedFilename) {
		a.download = derivedFilename;
	}

	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	window.URL.revokeObjectURL(blobUrl);
}

// Equivalent to lodash's or underscore's isEmpty, but with better typing and without the dependency
export function isEmpty(value: any): boolean {
	if (value === null || value === undefined) {
		return true;
	}

	if (typeof value === 'string' || Array.isArray(value)) {
		return value.length === 0;
	}

	if (typeof value === 'object') {
		return Object.keys(value).length === 0;
	}

	return false;
}

// Equivalent to lodash's or underscore's isEqual, but with better typing and without the dependency
export function isEqual(value1: any, value2: any): boolean {
	if (value1 === value2) {
		return true;
	}

	if (typeof value1 !== typeof value2) {
		return false;
	}

	if (typeof value1 === 'object' && typeof value2 === 'object') {
		const keys1 = Object.keys(value1);
		const keys2 = Object.keys(value2);

		if (keys1.length !== keys2.length) {
			return false;
		}

		for (const key of keys1) {
			if (!isEqual(value1[key], value2[key])) {
				return false;
			}
		}

		return true;
	}

	return false;
}

export function debounce(func: Function, duration: number, immediate = false) {
	let timeout: any;

	if (immediate) {
		return function (...args: any[]) {
			const callNow = !timeout;
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				timeout = null;
			}, duration);
			if (callNow) func.apply(this, args);
		};
	}

	return function (...args: any[]) {
		const effect = () => {
			timeout = null;
			return func.apply(this, args);
		};

		clearTimeout(timeout);
		timeout = setTimeout(effect, duration);
	};
}

// random uuid
export function makeId() {
	let text = '';
	const possible =
		'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

	for (let i = 0; i < 5; i++) {
		text += possible.charAt(Math.floor(Math.random() * possible.length));
	}

	return text;
}

/**
 * Returns true if the user is on macOS or an iOS device.
 */
export function isMacPlatform(): boolean {
  // 1. New Client Hints API (Chrome, Edge…)
  const uaData = (navigator as any).userAgentData;
  if (uaData && typeof uaData.platform === 'string') {
		const sanitizedPlatform = uaData.platform.toLowerCase();
    return sanitizedPlatform.startsWith('mac') || sanitizedPlatform.startsWith('iphone') || sanitizedPlatform.startsWith('ipad');
  }

  // 2. Classic navigator.platform (deprecated, but still widely supported)
  const plat = navigator.platform || '';
	const sanitizedPlat = plat.toLowerCase();
  if (/^mac/i.test(plat) || /^ip(hone|od|ad)/i.test(plat)) {
    return true;
  }

  // 3. Fallback to sniffing navigator.userAgent
  const ua = navigator.userAgent || '';
	const sanitizedUa = ua.toLowerCase();
  return /\bmac\b/.test(ua) || /\b(iphone|ipad|ipod)\b/.test(sanitizedUa);
}

/**
 * Extracts the simple Lambda function name from a full ARN or an API Gateway Integration URI.
 */
export function getFunctionNameFromArn(arnOrName: string): string {
	if (!arnOrName) return '';
	if (arnOrName.startsWith('arn:aws:lambda:')) {
		return arnOrName.split(':').pop() || '';
	}
	if (arnOrName.includes('/functions/arn:aws:lambda:')) {
		const match = arnOrName.match(/\/functions\/(arn:aws:lambda:[^/]+)/);
		if (match) {
			return match[1].split(':').pop() || '';
		}
	}
	if (arnOrName.includes('/functions/')) {
		const parts = arnOrName.split('/functions/');
		const funcPart = parts[1].split('/')[0];
		if (funcPart.startsWith('arn:aws:')) {
			return funcPart.split(':').pop() || '';
		}
		return funcPart;
	}
	return arnOrName;
}

